import React from "react";
import { IconCheck } from "@tabler/icons-react";
import { twMerge } from "tailwind-merge";
import type { WizardStep, WizardStepId } from "../../store/@types/portal";

type StepIndicatorProps = {
  steps: WizardStep[];
  currentStepId: WizardStepId;
};

const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentStepId,
}) => {
  const currentIndex = Math.max(
    0,
    steps.findIndex((step) => step.id === currentStepId),
  );
  const progressPercent =
    steps.length > 1 ? (currentIndex / (steps.length - 1)) * 100 : 0;
  const activeStep = steps[currentIndex];

  return (
    <div className="relative z-10 -mt-6 mx-auto max-w-6xl rounded-2xl bg-white px-4 py-5 shadow-lg shadow-slate-900/5 sm:-mt-10 sm:px-6 sm:py-6 lg:px-10">
      {/* Compact status line — mobile only, avoids cramming 4 long labels
          into a narrow viewport. Full per-step labels take over at sm+. */}
      <p className="mb-4 text-center text-sm font-medium text-slate-500 sm:hidden">
        Step {currentIndex + 1} of {steps.length}
        {activeStep && (
          <>
            : <span className="text-primary-500">{activeStep.label}</span>
          </>
        )}
      </p>

      <div className="relative flex items-start justify-between">
        <div className="absolute left-0 right-0 top-4 h-px bg-slate-200 sm:top-[18px] lg:top-6" />
        <div
          className="absolute left-0 top-4 h-px bg-primary-500 transition-all duration-500 sm:top-[18px] lg:top-6"
          style={{ width: `${progressPercent}%` }}
        />
        {steps.map((step, index) => {
          const isComplete = index < currentIndex;
          const isActive = index === currentIndex;

          return (
            <div
              key={step.id}
              className="relative flex flex-1 flex-col items-center gap-2 bg-white px-0.5 sm:px-2"
            >
              <hr className="absolute left-0 right-0 top-4 h-[2px] border-0 bg-gray-50/50 sm:top-[18px] lg:top-6" />
              <div
                className={twMerge(
                  "relative flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-colors sm:h-9 sm:w-9 sm:text-sm lg:h-12 lg:w-12",
                  isComplete && "bg-black text-white",
                  isActive && "bg-primary-500 text-white",
                  !isComplete &&
                    !isActive &&
                    "border-2 border-gray-50/50 bg-white text-slate-400",
                )}
              >
                {isComplete ? (
                  <IconCheck
                    className="h-4 w-4 sm:h-[18px] sm:w-[18px]"
                    stroke={2.5}
                  />
                ) : (
                  index + 1
                )}
              </div>
              <span
                className={twMerge(
                  "hidden text-center text-xs font-medium sm:block sm:whitespace-nowrap sm:text-sm lg:text-base",
                  isActive && "text-primary-500",
                  isComplete && "text-slate-900",
                  !isComplete && !isActive && "text-slate-400",
                )}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StepIndicator;
